import { useState } from "react"
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { ArrowLeft, ArrowUpRight, Check, Sparkles, TriangleAlert, X } from "lucide-react"
import { toast } from "sonner"
import type { ModerationRule } from "@vienna/contracts"
import { AutoHeading, Choice, DotBadge } from "@/components/automations/primitives"
import { accountOptions } from "@/components/brand/account-options"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { TagInput } from "@/components/ui/tag-input"
import { Textarea } from "@/components/ui/textarea"
import { api, ApiError } from "@/lib/api"
import { formatDateTime } from "@/lib/format"
import { channelsQuery, moderationRuleQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/automations/moderation/$ruleId")({
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData(channelsQuery(params.workspace))
    if (params.ruleId !== "new") await context.queryClient.ensureQueryData(moderationRuleQuery(params.workspace, params.ruleId))
  },
  component: RuleEditor,
})

const STATUS: Record<string, string> = { draft: "Draft", enabled: "Enabled", paused: "Paused", paused_access_missing: "Paused · access missing" }

function ActionRow({ checked, onChange, title, children }: { checked: boolean; onChange: (v: boolean) => void; title: string; children?: React.ReactNode }) {
  return (
    <label className="flex items-start gap-3 rounded-lg border p-3 has-[:checked]:border-foreground/30">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(v === true)} className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{title}</p>
        {children}
      </div>
    </label>
  )
}

/** A phrase rule: where it applies, what it matches, what it does, and a sample to try it on. */
function RuleEditor() {
  const { workspace } = useWorkspaceContext()
  const { ruleId } = Route.useParams()
  const isNew = ruleId === "new"
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const channels = useSuspenseQuery(channelsQuery(workspace.slug)).data.connections.filter((c) => c.status !== "disconnected")
  const existing = useQuery({ ...moderationRuleQuery(workspace.slug, ruleId), enabled: !isNew })
  const rule = existing.data ?? null
  const [name, setName] = useState(rule?.name ?? "Hide promotional spam")
  const [connectionId, setConnectionId] = useState(rule?.connectionId ?? channels[0]?.id ?? "")
  const [scope, setScope] = useState<"all_posts" | "selected_posts">(rule?.scope ?? "all_posts")
  const [postIds, setPostIds] = useState<string[]>(rule?.postIds ?? [])
  const [priority, setPriority] = useState(String(rule?.priority ?? 100))
  const [phraseList, setPhraseList] = useState<string[]>(rule?.phrases ?? ["promote it on", "DM us for promotion", "buy followers"])
  const [hide, setHide] = useState(rule?.actions.hide ?? true)
  const [tagName, setTagName] = useState(rule?.actions.tagName ?? "possible-spam")
  const [tag, setTag] = useState(Boolean(rule?.actions.tagName ?? true))
  const [notify, setNotify] = useState(rule?.actions.notify ?? false)
  const [sample, setSample] = useState("Promote it on our page! DM us for promotion.")
  const [tested, setTested] = useState<string | null | undefined>(undefined)
  const params = { workspace: workspace.slug }
  const connection = channels.find((c) => c.id === connectionId)
  const accessOk = connection?.capabilities.read_comments === "available"
  const payload = { connectionId, name: name.trim(), scope, postIds, phrases: phraseList, actions: { hide, tagName: tag ? tagName.trim() || null : null, notify }, priority: Math.min(1000, Math.max(1, Number(priority) || 100)) }
  const describe = (error: unknown) => (error instanceof ApiError ? error.problem.title : "Something went wrong")

  const save = useMutation({
    mutationFn: () => api<ModerationRule>(isNew ? `/w/${workspace.slug}/moderation/rules` : `/w/${workspace.slug}/moderation/rules/${ruleId}`, { method: isNew ? "POST" : "PUT", body: JSON.stringify(payload) }),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug, "moderation"] })
      toast.success(isNew ? "Draft saved" : `Saved · revision ${saved.revision}`)
      if (isNew) await navigate({ to: "/w/$workspace/automations/moderation/$ruleId", params: { ...params, ruleId: saved.id }, replace: true })
    },
    onError: (error) => toast.error(describe(error)),
  })
  const status = useMutation({
    mutationFn: (next: "enabled" | "paused" | "draft") => api<ModerationRule>(`/w/${workspace.slug}/moderation/rules/${ruleId}/status`, { method: "POST", body: JSON.stringify({ status: next }) }),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug, "moderation"] })
      toast.success(saved.status === "enabled" ? "Rule enabled" : saved.status === "paused" ? "Rule paused" : "Rule reverted to draft")
    },
    onError: (error) => toast.error(describe(error)),
  })
  const test = useMutation({
    mutationFn: () => api<{ matched: string | null }>(`/w/${workspace.slug}/moderation/test`, { method: "POST", body: JSON.stringify({ phrases: phraseList, sample }) }),
    onSuccess: (r) => setTested(r.matched),
  })
  const actionSummary = [hide && "hide the comment", tag && "add the tag", notify && "flag it for the team"].filter(Boolean).join(", ")

  return (
    <div className="page-wrap auto-page">
      <AutoHeading
        title={name.trim() || "Untitled rule"}
        description={rule ? `Revision ${rule.revision} · last edited by ${rule.lastEditedByName}, ${formatDateTime(rule.updatedAt)}` : "A small rule for repetitive comment work. Save it as a draft, try a sample, then enable it."}
        back={
          <Button variant="ghost" size="sm" className="mb-3 -ml-2" asChild>
            <Link to="/w/$workspace/automations/moderation" params={params}>
              <ArrowLeft />
              Moderation
            </Link>
          </Button>
        }
      >
        <DotBadge warn={rule?.effectiveStatus === "paused_access_missing"}>{rule ? (STATUS[rule.effectiveStatus] ?? rule.effectiveStatus) : "Draft"}</DotBadge>
        <Button variant="outline" asChild>
          <Link to="/w/$workspace/automations/moderation" params={params}>
            Cancel
          </Link>
        </Button>
        <Button onClick={() => save.mutate()} disabled={save.isPending || !name.trim() || !connectionId || phraseList.length === 0}>
          {isNew ? "Save draft" : "Save changes"}
        </Button>
      </AutoHeading>

      <div className="auto-two-col">
        <div className="space-y-5">
          <Card>
            <CardHeader>
              <CardTitle>Where it applies</CardTitle>
              <CardDescription>Rules are scoped to one connected account. Lower priority numbers run first.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5 sm:col-span-2">
                <Label htmlFor="rule-name">Rule name</Label>
                <Input id="rule-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
              </div>
              <div className="grid gap-1.5">
                <Label>Channel account</Label>
                <Choice label="Channel account" value={connectionId} onChange={setConnectionId} values={accountOptions(channels, "comments")} />
              </div>
              <div className="grid gap-1.5">
                <Label>Apply to</Label>
                <Choice label="Apply to" value={scope} onChange={setScope} values={[{ value: "all_posts", label: "Comments on all posts" }, { value: "selected_posts", label: "Comments on selected posts" }]} />
              </div>
              {scope === "selected_posts" && (
                <div className="grid gap-1.5 sm:col-span-2">
                  <Label htmlFor="rule-posts">Post ids</Label>
                  <TagInput id="rule-posts" value={postIds} onChange={setPostIds} placeholder="Type a provider media id and press Enter" />
                </div>
              )}
              <div className="grid gap-1.5">
                <Label htmlFor="rule-priority">Priority</Label>
                <Input id="rule-priority" type="number" min={1} max={1000} value={priority} onChange={(e) => setPriority(e.target.value)} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>When a comment contains…</CardTitle>
              <CardDescription>Any of these phrases, ignoring capitalisation. Type one and press Enter to add it.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <TagInput
                aria-label="Phrases"
                value={phraseList}
                onChange={(next) => {
                  setPhraseList(next)
                  setTested(undefined)
                }}
                placeholder="promote it on"
              />
              {phraseList.length === 0 && <p className="text-xs text-muted-foreground">Add at least one phrase.</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Then take these actions</CardTitle>
              <CardDescription>Hides go through the provider like any reply and can be undone from the comment.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3">
              <ActionRow checked={hide} onChange={setHide} title="Hide the matching comment">
                <p className="text-xs text-muted-foreground">Keeps it out of the public conversation; the original text stays in Vienna.</p>
              </ActionRow>
              <ActionRow checked={tag} onChange={setTag} title="Add a contact tag">
                <Input value={tagName} onChange={(e) => setTagName(e.target.value)} disabled={!tag} className="mt-2 max-w-60" aria-label="Tag name" maxLength={40} />
              </ActionRow>
              <ActionRow checked={notify} onChange={setNotify} title="Flag it for the team">
                <p className="text-xs text-muted-foreground">Admins and support managers get a notification linking to the comment.</p>
              </ActionRow>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-5 self-start">
          <Card>
            <CardHeader>
              <CardTitle>Try a sample</CardTitle>
              <CardDescription>Test the rule without changing live comments.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                aria-label="Sample comment"
                value={sample}
                onChange={(e) => {
                  setSample(e.target.value)
                  setTested(undefined)
                }}
                className="min-h-24"
              />
              <Button variant="outline" className="w-full" disabled={!sample.trim() || phraseList.length === 0 || test.isPending} onClick={() => test.mutate()}>
                <Sparkles />
                Test matching
              </Button>
              {tested !== undefined && (
                <div className="rounded-lg border bg-muted/40 p-3 text-sm" role="status">
                  <p className="flex items-center gap-2 font-medium">
                    {tested ? <Check className="size-4" /> : <X className="size-4" />}
                    {tested ? "Match found" : "No match"}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">{tested ? `Matched “${tested}”. ${actionSummary ? `Would ${actionSummary}.` : "Choose at least one action."}` : "This comment would be left unchanged."}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {accessOk ? <Check className="size-4" /> : <TriangleAlert className="size-4 text-amber-600" />}
                {accessOk ? (rule?.status === "enabled" ? "Rule is live" : "Ready to enable") : "Restore access to enable"}
              </CardTitle>
              <CardDescription>
                {accessOk
                  ? `${connection?.handle ?? connection?.displayName} has comment access. ${isNew ? "Save the draft first." : rule?.status === "enabled" ? "Pause it any time." : "Enable to start acting on new comments."}`
                  : connection
                    ? `${connection.handle ?? connection.displayName} cannot run moderation rules while comment permission is missing.`
                    : "Choose a connected account."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {accessOk ? (
                rule?.status === "enabled" ? (
                  <Button className="w-full" variant="outline" onClick={() => status.mutate("paused")} disabled={status.isPending}>
                    Pause rule
                  </Button>
                ) : (
                  <Button className="w-full" onClick={() => status.mutate("enabled")} disabled={isNew || status.isPending || phraseList.length === 0}>
                    Enable rule
                  </Button>
                )
              ) : (
                <>
                  <Button className="w-full" disabled>
                    Enable rule
                  </Button>
                  {connection && (
                    <Button className="w-full" variant="outline" asChild>
                      <Link to="/w/$workspace/channels/$connectionId" params={{ ...params, connectionId: connection.id }}>
                        Review connection
                        <ArrowUpRight />
                      </Link>
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
